/**
 * Meta Marketing API display helpers.
 *
 * Mirrors `./googleAds.ts` in scope and naming — only display-side
 * concerns belong here (e.g. stripping the `act_` namespace for the
 * UI). Currency conversion is not required for Meta: the ingest writes
 * dollars directly to `meta_ads_data.*_daily` (see migration 007).
 */

/**
 * Format Meta ad account ID for display by stripping the `act_` prefix.
 *
 * Meta's API uses `act_<digits>` as the canonical ad account ID. The
 * backend (Marketing API + our own `meta_ads_credentials` table) still
 * needs the prefix, but Meta's own surfaces — Ads Manager URLs, the
 * Meta dashboard, support tickets — elide it for display. Match that
 * convention so a user comparing IDs across tools doesn't see two
 * different-looking strings for the same account.
 *
 * Named to match `formatAccountId` from `./googleAds.ts` so call sites
 * import them as
 *   `import { formatAccountId } from '@/utils/metaAds';`
 * or, when both are needed in one file,
 *   `import { formatAccountId as formatGoogleAccountId } from '@/utils/googleAds';`
 *   `import { formatAccountId as formatMetaAccountId } from '@/utils/metaAds';`
 *
 * @param accountId - Meta ad account ID (e.g. "act_977198964917414").
 *                    Can be null/undefined.
 * @returns Display string. Returns "Unknown" if null/undefined,
 *          mirroring `formatAccountId` in `./googleAds.ts`.
 *
 * @example
 * formatAccountId("act_977198964917414")  // Returns "977198964917414"
 * formatAccountId(null)                   // Returns "Unknown"
 * formatAccountId("977198964917414")      // Returns "977198964917414" (unchanged if no prefix)
 * formatAccountId("act_")                 // Returns "" (prefix-only edge case)
 */
export function formatAccountId(accountId: string | undefined | null): string {
  if (!accountId) return 'Unknown';
  return accountId.replace(/^act_/, '');
}
